import { createContext, type ComponentChildren } from "preact";
import {
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from "preact/hooks";
import { useLocation } from "preact-iso";
import { useConfirm } from "./use-confirm";
import { confirmDiscardNavigation } from "./confirm-discard-navigation";

type NavigationGuard = {
  // Registers (or clears) one editor's unsaved state under a stable key.
  setDirty: (key: string, dirty: boolean) => void;
  // For navigations that aren't <a> clicks (sign-out, mode switch, any
  // window.location change): confirms when something is dirty and, once
  // confirmed, lets the resulting page unload without a second prompt.
  confirmLeave: () => Promise<boolean>;
};

const NavigationGuardContext = createContext<NavigationGuard>({
  setDirty: () => {},
  confirmLeave: async () => true,
});

// One guard for every way out of a dirty document, so each link or button no
// longer needs its own check:
// - same-origin <a> clicks: intercepted in the window *capture* phase, ahead
//   of preact-iso's window-level click router (which ignores
//   defaultPrevented), then replayed with link.click() once confirmed so the
//   link's own handlers still run;
// - back/forward: popstate is stopped before preact-iso sees it, the editor
//   URL is pushed back, and a confirmed leave replays history.back();
// - reloads / full-page navigations: beforeunload.
export function NavigationGuardProvider({
  children,
}: {
  children: ComponentChildren;
}) {
  const location = useLocation();
  const { confirm, dialog } = useConfirm();
  // useConfirm hands out a new function each render; read it through a ref so
  // the listeners and the context value stay stable.
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;
  const dirtyKeys = useRef(new Set<string>());
  // Set while replaying a confirmed navigation so the listeners let it through.
  const bypass = useRef(false);
  // Set once a leave is confirmed; beforeunload must not prompt again.
  const allowUnload = useRef(false);
  // URL of the page the guard is protecting. Updated on render, i.e. only
  // after preact-iso has actually routed — so during a popstate it still
  // holds the editor's URL.
  const guardedUrl = useRef(location.url);
  guardedUrl.current = location.url;

  const isDirty = () => dirtyKeys.current.size > 0;

  useEffect(() => {
    allowUnload.current = false;
  }, [location.url]);

  // Layout effect for the same reason as useRegisterDirty: listen from the
  // first commit instead of an animation frame later.
  useLayoutEffect(() => {
    async function onClick(e: MouseEvent) {
      if (bypass.current || !isDirty()) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e
        .composedPath()
        .find((el): el is HTMLAnchorElement => el instanceof HTMLAnchorElement && !!el.href);
      if (!link || link.download) return;
      if (link.target && !/^_?self$/i.test(link.target)) return;
      const href = link.getAttribute("href") ?? "";
      if (href.startsWith("#") || link.href === window.location.href) return;

      e.preventDefault();
      e.stopPropagation();
      if (!(await confirmDiscardNavigation(confirmRef.current))) return;
      allowUnload.current = true;
      bypass.current = true;
      try {
        link.click();
      } finally {
        bypass.current = false;
      }
    }

    async function onPopState(e: PopStateEvent) {
      if (bypass.current) {
        bypass.current = false;
        return;
      }
      if (!isDirty()) return;
      // Capture listeners on the target run before preact-iso's listener, so
      // this keeps it from rendering the previous page.
      e.stopImmediatePropagation();
      history.pushState(null, "", guardedUrl.current);
      if (!(await confirmDiscardNavigation(confirmRef.current))) return;
      allowUnload.current = true;
      bypass.current = true;
      history.back();
    }

    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!isDirty() || allowUnload.current) return;
      e.preventDefault();
      e.returnValue = "";
    }

    window.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onPopState, true);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPopState, true);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  const value = useMemo<NavigationGuard>(
    () => ({
      setDirty(key, dirty) {
        if (dirty) dirtyKeys.current.add(key);
        else dirtyKeys.current.delete(key);
      },
      async confirmLeave() {
        if (!isDirty()) return true;
        const ok = await confirmDiscardNavigation(confirmRef.current);
        if (ok) allowUnload.current = true;
        return ok;
      },
    }),
    [],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {dialog}
      {children}
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard(): NavigationGuard {
  return useContext(NavigationGuardContext);
}

// Reports a component's unsaved state to the guard for as long as it is mounted.
// Layout effects run right after the commit; Preact defers plain effects to an
// animation frame, which leaves a window (and, in a throttled background tab,
// an indefinite one) where a just-dirtied draft isn't guarded yet.
export function useRegisterDirty(dirty: boolean): void {
  const key = useId();
  const { setDirty } = useNavigationGuard();
  useLayoutEffect(() => {
    setDirty(key, dirty);
  }, [key, dirty, setDirty]);
  useLayoutEffect(() => () => setDirty(key, false), [key, setDirty]);
}
